import { Fragment, useState, useEffect } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { Menu, Transition } from '@headlessui/react';
import clsx from 'clsx';
import {
  Bars3Icon, XMarkIcon, ChevronUpDownIcon,
  ArrowRightOnRectangleIcon, KeyIcon,
  ChevronLeftIcon, ChevronRightIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../../context/AuthContext';
import { visibleSections } from '../../config/navVisibility';
import { ROLE_LABELS, ROLES } from '../../config/rolePermissions';
import { contentCockpitService } from '../../services';
import { NAV_SECTIONS } from './navConfig';
import Logo from '../ui/Logo';
import NavbarQuickActions from './NavbarQuickActions';

const STORAGE_KEY = 'sidenav-collapsed';
const SECTIONS_STORAGE_KEY = 'sidenav-sections-collapsed';
const PENDING_POLL_MS = 45_000;

function isItemActive(pathname, href) {
  return pathname === href || (href !== '/' && pathname.startsWith(`${href}/`));
}

/**
 * Which nav item owns the current route: the LONGEST matching href wins.
 *
 * A plain prefix match lights up every ancestor too, so /clients/readiness marked
 * both "Readiness" and "Clients" as current. Resolving the best match once, across
 * all items, keeps exactly one highlighted — and holds for any nested route added
 * later rather than needing a special case per pair.
 */
function resolveActiveHref(pathname, sections) {
  let best = null;
  for (const section of sections) {
    for (const item of section.items) {
      if (!isItemActive(pathname, item.href)) continue;
      if (!best || item.href.length > best.length) best = item.href;
    }
  }
  return best;
}

function loadSectionCollapsed() {
  try {
    const raw = localStorage.getItem(SECTIONS_STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch {
    // ignore
  }
  return {};
}

function useNavSections() {
  const { user } = useAuth();
  return visibleSections(NAV_SECTIONS, user);
}

/** Admin-only: pending Content Studio generation unlock requests. */
function usePendingRequestsBadge() {
  const { user } = useAuth();
  const location = useLocation();
  const [count, setCount] = useState(0);
  const isAdmin = user?.role === ROLES.ADMIN;

  useEffect(() => {
    if (!isAdmin) {
      setCount(0);
      return undefined;
    }
    let cancelled = false;
    const refresh = () => {
      contentCockpitService.pendingGenerationCount()
        .then((r) => {
          if (!cancelled) setCount(Number(r.data?.pendingCount) || 0);
        })
        .catch(() => {
          if (!cancelled) setCount(0);
        });
    };
    refresh();
    const id = setInterval(refresh, PENDING_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [isAdmin, location.pathname]);

  return count;
}

function UserMenu({ collapsed }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const initials = (user?.name || '?').split(' ').map((s) => s[0]).slice(0, 2).join('').toUpperCase();

  return (
    <Menu as="div" className="relative">
      <Menu.Button className={clsx(
        'w-full flex items-center gap-2.5 rounded-xl p-1.5 hover:bg-surface-100 transition-colors ring-1 ring-transparent hover:ring-surface-200',
        collapsed && 'justify-center',
      )}>
        <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-brand-400 to-brand-600 text-white flex items-center justify-center text-xs font-bold shadow-md shadow-brand-500/20 shrink-0">
          {initials}
        </div>
        {!collapsed && (
          <>
            <div className="text-left min-w-0 flex-1 leading-tight">
              <p className="text-sm font-semibold text-dark-900 truncate">{user?.name}</p>
              <p className="text-2xs text-surface-500 truncate">
                {ROLE_LABELS[user?.role] || user?.role}
                {user?.departmentId?.name ? ` · ${user.departmentId.name}` : ''}
              </p>
            </div>
            <ChevronUpDownIcon className="h-4 w-4 text-surface-400 shrink-0" />
          </>
        )}
      </Menu.Button>
      <Transition as={Fragment}
        enter="transition ease-out duration-100" enterFrom="transform opacity-0 scale-95" enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75" leaveFrom="transform opacity-100 scale-100" leaveTo="transform opacity-0 scale-95">
        <Menu.Items className="absolute bottom-full mb-2 left-0 w-56 origin-bottom-left rounded-2xl bg-white shadow-card-hover border border-surface-100 p-1.5 focus:outline-none z-50">
          <div className="px-3 py-2 border-b border-surface-100">
            <p className="text-sm font-semibold text-dark-900 truncate">{user?.name}</p>
            <p className="text-2xs text-surface-500 truncate">{user?.email}</p>
          </div>
          <Menu.Item>
            {({ active }) => (
              <button type="button" onClick={() => navigate('/settings/security')}
                className={`w-full flex items-center gap-2 px-3 py-2 rounded-xl text-sm ${active ? 'bg-surface-50' : ''} text-surface-700`}>
                <KeyIcon className="h-4 w-4" /> Change password
              </button>
            )}
          </Menu.Item>
          <Menu.Item>
            {({ active }) => (
              <button type="button" onClick={logout}
                className={`w-full flex items-center gap-2 px-3 py-2 rounded-xl text-sm ${active ? 'bg-error-50' : ''} text-error-600`}>
                <ArrowRightOnRectangleIcon className="h-4 w-4" /> Sign out
              </button>
            )}
          </Menu.Item>
        </Menu.Items>
      </Transition>
    </Menu>
  );
}

function NavItems({ sections, collapsed, onNavigate, pendingRequests = 0 }) {
  const location = useLocation();
  const [sectionCollapsed, setSectionCollapsed] = useState(loadSectionCollapsed);

  useEffect(() => {
    try {
      localStorage.setItem(SECTIONS_STORAGE_KEY, JSON.stringify(sectionCollapsed));
    } catch {
      // ignore
    }
  }, [sectionCollapsed]);

  // Expand the section that contains the current route
  useEffect(() => {
    setSectionCollapsed((prev) => {
      let next = prev;
      for (const section of sections) {
        const hasActive = section.items.some((item) => isItemActive(location.pathname, item.href));
        if (hasActive && prev[section.label]) {
          if (next === prev) next = { ...prev };
          next[section.label] = false;
        }
      }
      return next;
    });
  }, [location.pathname, sections]);

  const toggleSection = (label) => {
    setSectionCollapsed((prev) => ({
      ...prev,
      [label]: !prev[label],
    }));
  };

  const isSectionOpen = (label) => sectionCollapsed[label] !== true;

  // Resolved once per route so exactly one item can be current.
  const activeHref = resolveActiveHref(location.pathname, sections);

  return (
    <nav className="flex-1 overflow-y-auto overflow-x-hidden px-3 py-3 space-y-1 scrollbar-hide">
      {sections.map((section, si) => {
        const open = isSectionOpen(section.label);

        return (
          <div key={section.label} className={si > 0 ? 'pt-3' : ''}>
            {collapsed ? (
              si > 0 && <div className="border-t border-surface-100 mb-2" />
            ) : (
              <button
                type="button"
                onClick={() => toggleSection(section.label)}
                className="w-full flex items-center justify-between px-2 py-1.5 mb-1 rounded-lg hover:bg-surface-100 transition-colors group"
                aria-expanded={open}
              >
                <span className="text-2xs font-semibold uppercase tracking-wider text-surface-400 group-hover:text-surface-600">
                  {section.label}
                </span>
                <ChevronRightIcon
                  className={clsx(
                    'h-3.5 w-3.5 text-surface-400 group-hover:text-surface-500 transition-transform duration-200 shrink-0',
                    open && 'rotate-90',
                  )}
                />
              </button>
            )}

            {(collapsed || open) && (
              <div className="space-y-0.5">
                {section.items.map((item) => {
                  const showDot = item.pendingBadge && pendingRequests > 0;
                  const showLiveDot = item.liveDot;
                  // Our own match, not NavLink's — NavLink treats any descendant
                  // path as active, which highlighted the parent item too.
                  const isActive = item.href === activeHref;
                  return (
                    <NavLink
                      key={item.href}
                      to={item.href}
                      onClick={onNavigate}
                      title={collapsed ? item.name : undefined}
                      className={clsx(
                        'flex items-center gap-3 rounded-xl text-sm font-medium transition-colors',
                        collapsed ? 'justify-center px-0 py-2.5' : 'px-3 py-2.5',
                        isActive
                          ? 'bg-brand-50 text-brand-700 font-semibold'
                          : 'text-surface-600 hover:text-dark-900 hover:bg-surface-100',
                      )}
                    >
                      <>
                          <span className="relative shrink-0">
                            <item.icon
                              className={clsx(
                                'h-5 w-5',
                                isActive ? 'text-brand-600' : 'text-surface-400',
                              )}
                            />
                            {collapsed && showDot && (
                              <span
                                className="absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full bg-rose-500 ring-2 ring-white"
                                aria-label={`${pendingRequests} pending`}
                              />
                            )}
                            {collapsed && showLiveDot && (
                              <span
                                className="absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full bg-emerald-500 ring-2 ring-white animate-live-blink"
                                aria-hidden
                              />
                            )}
                          </span>
                          {!collapsed && (
                            <span className="flex min-w-0 flex-1 items-center gap-2">
                              <span className="truncate">{item.name}</span>
                              {showDot && (
                                <span
                                  className="h-2 w-2 shrink-0 rounded-full bg-rose-500"
                                  aria-label={`${pendingRequests} unread request${pendingRequests === 1 ? '' : 's'}`}
                                />
                              )}
                              {showLiveDot && (
                                <span
                                  className="h-2 w-2 shrink-0 rounded-full bg-emerald-500 animate-live-blink"
                                  aria-hidden
                                />
                              )}
                            </span>
                          )}
                      </>
                    </NavLink>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </nav>
  );
}

export default function Sidebar() {
  const sections = useNavSections();
  const pendingRequests = usePendingRequestsBadge();
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) === '1'; } catch { return false; }
  });
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, collapsed ? '1' : '0'); } catch { /* ignore */ }
  }, [collapsed]);

  return (
    <>
      {/* Desktop sidebar */}
      <aside
        className={clsx(
          'hidden lg:flex flex-col shrink-0 h-full min-h-0 relative overflow-visible bg-white border-r border-surface-200/70 transition-[width] duration-200 ease-out no-print',
          collapsed ? 'w-[72px]' : 'w-64',
        )}
      >
        {/* Menu header — logo / title */}
        <div
          className={clsx(
            'h-16 flex items-center border-b border-surface-200/70 shrink-0',
            collapsed ? 'justify-center px-2' : 'px-4',
          )}
        >
          <NavLink
            to="/dashboard"
            className={clsx(
              'flex items-center min-w-0 group',
              collapsed ? 'justify-center' : 'gap-2.5',
            )}
            title="Dashboard"
          >
            <Logo variant="dark" className="h-8 w-auto max-w-[120px] object-contain shrink-0" />
            {!collapsed && (
              <span className="text-base font-bold text-dark-900 tracking-tight truncate">TaskOps</span>
            )}
          </NavLink>
        </div>

        <button
          type="button"
          onClick={() => setCollapsed((c) => !c)}
          className="absolute -right-3 top-[4.5rem] z-20 flex h-6 w-6 items-center justify-center rounded-full border border-surface-200 bg-white text-surface-600 shadow-sm transition-colors hover:bg-surface-50 hover:text-dark-900"
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? (
            <ChevronRightIcon className="h-3.5 w-3.5" />
          ) : (
            <ChevronLeftIcon className="h-3.5 w-3.5" />
          )}
        </button>

        <NavItems sections={sections} collapsed={collapsed} pendingRequests={pendingRequests} />

        <div className="border-t border-surface-200/70 p-2 shrink-0">
          <UserMenu collapsed={collapsed} />
        </div>
      </aside>

      {/* Mobile top bar (fixed; spans full width above content) */}
      <header className="lg:hidden fixed top-0 inset-x-0 z-30 h-14 bg-white border-b border-surface-200 flex items-center gap-3 px-3 no-print">
        <button type="button" onClick={() => setDrawerOpen(true)} className="relative p-2 -ml-1 rounded-xl text-surface-600 hover:bg-surface-100" aria-label="Open menu">
          <Bars3Icon className="h-6 w-6" />
          {pendingRequests > 0 && (
            <span className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-rose-500 ring-2 ring-white" />
          )}
        </button>
        <NavLink to="/dashboard" className="flex items-center gap-2 min-w-0">
          <Logo variant="dark" className="h-7 w-auto max-w-[110px] object-contain" />
        </NavLink>
        <div className="flex-1" />
        <NavbarQuickActions />
        <UserMenu collapsed />
      </header>

      {/* Mobile drawer */}
      {drawerOpen && (
        <div className="fixed inset-0 z-40 bg-dark-950/60 lg:hidden" onClick={() => setDrawerOpen(false)} aria-hidden />
      )}
      <aside className={clsx(
        'fixed inset-y-0 left-0 z-50 w-72 bg-white border-r border-surface-200 flex flex-col lg:hidden transform transition-transform duration-300 ease-out',
        drawerOpen ? 'translate-x-0' : '-translate-x-full',
      )}>
        <div className="h-14 px-4 flex items-center justify-between border-b border-surface-200">
          <Logo variant="dark" className="h-7 w-auto max-w-[110px] object-contain" />
          <button type="button" onClick={() => setDrawerOpen(false)} className="p-2 rounded-xl text-surface-500 hover:bg-surface-100" aria-label="Close menu">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
        <NavItems sections={sections} collapsed={false} onNavigate={() => setDrawerOpen(false)} pendingRequests={pendingRequests} />
        <div className="border-t border-surface-200 p-2"><UserMenu collapsed={false} /></div>
      </aside>
    </>
  );
}
